/**
 * Adzuki.AttachmentDrag
 *
 * Drag-and-drop reordering for the attachment tables (TS-314, TS-316, TS-317).
 *
 * Shared rather than written per list because the attachment tables have three
 * traps that are each invisible when you get them wrong, and a second copy is
 * how one of them gets fixed and the other does not. See
 * docs/kb/trip-summary/attachments.md.
 */

(function AttachmentDrag($, Adzuki)
{
	"use strict";

	var attachmentDrag = {};

	/**
	 * Clear Bootstrap tooltips around a drag.
	 *
	 * The drag handle carries a tooltip, and dragula clones the row while Vue
	 * replaces it on reorder -- so the trigger node disappears without ever
	 * getting a mouseleave, and its tip element is left behind. Do that a few
	 * times and they stack up visibly.
	 *
	 * hide() only reaches triggers that still exist, so orphans have to be
	 * removed outright. Bootstrap rebuilds the tip element the next time one is
	 * shown, so this is safe.
	 */
	function clearTooltips()
	{
		try {
			$('[data-toggle="tooltip"]').tooltip('hide');
		} catch (e) {
			// the tooltip plugin is not initialised on every page that lists files
		}

		$('.tooltip').remove();
	}

	/**
	 * Wire dragula to one attachment table.
	 *
	 * @param  {Element}  listEl     the <ul> holding the rows
	 * @param  {Function} onReorder  called with the new id order, newest state first
	 * @return {Object|null}         the drake, or null when dragula is unavailable
	 */
	attachmentDrag.create = function create(listEl, onReorder)
	{
		var drake;
		// where the row sat before the drag, so the DOM can be put back
		var originalNextSibling = null;

		if (!listEl || typeof dragula === 'undefined') {
			return null;
		}

		drake = dragula([listEl], {
			// only the handle drags, so the Display checkbox and the action icons
			// stay clickable
			moves: function moves(el, source, handle) {
				return handle.closest('.ts-attachment-drag') !== null;
			},
			// the header row is not an attachment
			invalid: function invalid(el) {
				return el.classList.contains('table-header');
			},
			revertOnSpill: true,
		});

		// TRAP 1. The rows are display:table-row and the mirror is cloned onto
		// <body>, where it has no table to size against and collapses to nothing.
		// Copy the measured widths across so the thing under the cursor still looks
		// like the row being dragged.
		drake.on('cloned', function cloned(clone, original, type) {
			var originalCells;

			if (type !== 'mirror') {
				return;
			}

			originalCells = original.children;

			clone.style.display = 'table';
			clone.style.width   = original.offsetWidth + 'px';

			Array.prototype.forEach.call(clone.children, function eachCell(cell, i) {
				if (originalCells[i]) {
					cell.style.display = 'table-cell';
					cell.style.width   = originalCells[i].offsetWidth + 'px';
				}
			});
		});

		drake.on('drag', function drag(el) {
			originalNextSibling = el.nextElementSibling;

			// the handle's own tooltip is showing at this point, and its trigger is
			// about to be cloned and moved out from under it
			clearTooltips();
		});

		// fires for a completed drop AND a cancelled drag, so strays are swept
		// either way
		drake.on('dragend', function dragend() {
			clearTooltips();
		});

		drake.on('drop', function drop(el, target) {
			var orderedIds;

			if (!target) {
				return;
			}

			orderedIds = Array.prototype.map.call(
				target.querySelectorAll('[data-attachment-id]'),
				function eachRow(row) {
					return row.dataset.attachmentId;
				}
			);

			// TRAP 2. Put the row back where Vue last rendered it BEFORE the caller
			// touches state. Vue patches its old vdom against the live DOM, and
			// dragula has already moved the node -- so without this, Vue applies
			// moves computed from the old order to a DOM already in the new one and
			// the rows scramble.
			//
			// drake.cancel() does NOT do this: by the time drop fires the drag is
			// complete and there is nothing left to revert.
			target.insertBefore(el, originalNextSibling);
			originalNextSibling = null;

			onReorder(orderedIds);
		});

		return drake;
	};

	/**
	 * Re-wire when the table element has been swapped out, and do nothing when it
	 * has not.
	 *
	 * TRAP 3. The table sits behind a v-if, so it does not exist while the list is
	 * empty and appears when the first attachment lands. A drake still pointing at
	 * a detached element silently stops responding, so callers re-run this from a
	 * watcher rather than only from mounted().
	 *
	 * Callers must also reach the list by ref, NOT by querying their root element:
	 * the attachment components have several root nodes, so they are fragments and
	 * the root property is only the FIRST of them.
	 *
	 * @param  {Object|null}  drake      the existing drake, if any
	 * @param  {Element}      listEl
	 * @param  {Function}     onReorder
	 * @return {Object|null}             the drake to keep
	 */
	attachmentDrag.refresh = function refresh(drake, listEl, onReorder)
	{
		if (drake && drake.containers[0] === listEl) {
			return drake;
		}

		if (drake) {
			drake.destroy();
		}

		return attachmentDrag.create(listEl, onReorder);
	};

	Adzuki.AttachmentDrag = attachmentDrag;
}(jQuery, window.Adzuki || {}));
